import React, { useState, useRef, ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { X, Moon, Sun } from 'lucide-react';

interface WindowProps {
  title: string;
  children: ReactNode;
  onClose?: () => void;
  initialX?: number;
  initialY?: number;
  width?: number;
  height?: number;
  supportDark?: boolean;
  zIndex?: number;
  onFocus?: () => void;
}

export function Window({
  title,
  children,
  onClose,
  initialX,
  initialY,
  width = 500,
  height = 400,
  supportDark = false,
  zIndex: basezIndex = 1000,
  onFocus,
}: WindowProps) {
  const [position, setPosition] = useState({
    x: initialX ?? Math.max(0, (window.innerWidth - width) / 2),
    y: initialY ?? Math.max(0, (window.innerHeight - height) / 2),
  });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [isDarkMode, setIsDarkMode] = useState(false);
  const windowRef = useRef<HTMLDivElement>(null);

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (windowRef.current) {
      const rect = windowRef.current.getBoundingClientRect();
      setDragOffset({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top,
      });
      setIsDragging(true);
      // onFocus?.();
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    // Allow drag interaction on Enter/Space (for accessibility)
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleMouseDown(e as unknown as React.MouseEvent<HTMLDivElement>);
    }
  };

  React.useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        setPosition({
          x: e.clientX - dragOffset.x,
          y: e.clientY - dragOffset.y,
        });
      }
    };
    if (isDragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);

      return () => {
        window.removeEventListener('mousemove', handleMouseMove);
        window.removeEventListener('mouseup', handleMouseUp);
      };
    }
    return () => {};
  }, [isDragging, dragOffset]);

  return createPortal(
    <div
      ref={windowRef}
      onMouseDown={() => onFocus?.()}
      role="none"
      className={`absolute border shadow-lg flex flex-col ${isDarkMode ? 'bg-gray-900 border-gray-700 text-white' : 'bg-white border-gray-300'}`}
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
        width: `${width}px`,
        height: `${height}px`,
        zIndex: isDragging ? basezIndex + 1 : basezIndex,
      }}
    >
      {/* Title bar */}
      <div
        role="button"
        tabIndex={0}
        onMouseDown={handleMouseDown}
        onKeyDown={handleKeyDown}
        className={`border-b px-4 py-2.5 flex items-center justify-between cursor-move select-none ${
          isDarkMode
            ? `border-gray-700 bg-gray-800 ${isDragging ? 'bg-gray-700' : ''}`
            : `border-gray-300 bg-gray-50 ${isDragging ? 'bg-gray-100' : ''}`
        }`}
      >
        <h2
          className={`text-sm font-medium ${
            isDarkMode ? 'text-gray-100' : 'text-gray-900'
          }`}
        >
          {title}
        </h2>
        <div className="flex items-center gap-1">
          {supportDark && (
            <button
              onClick={() => setIsDarkMode(!isDarkMode)}
              className={`p-1 rounded transition-colors ${
                isDarkMode
                  ? 'hover:bg-gray-700 text-gray-400'
                  : 'hover:bg-gray-200 text-gray-600'
              }`}
              title={isDarkMode ? 'Light mode' : 'Dark mode'}
            >
              {isDarkMode ? (
                <Sun size={16} strokeWidth={1.5} />
              ) : (
                <Moon size={16} strokeWidth={1.5} />
              )}
            </button>
          )}
          {onClose && (
            <button
              onClick={onClose}
              className={`p-1 rounded transition-colors ${
                isDarkMode
                  ? 'hover:bg-gray-700 text-gray-400'
                  : 'hover:bg-gray-200 text-gray-600'
              }`}
              title="Close"
            >
              <X size={16} strokeWidth={1.5} />
            </button>
          )}
        </div>
      </div>

      {/* Content */}
      <div
        className={`flex-1 overflow-auto ${
          isDarkMode ? 'bg-gray-900' : 'bg-white'
        }`}
      >
        {children}
      </div>
    </div>,
    document.body,
  );
}
